import React, { useEffect, useMemo, useState } from 'react';
import { Col, Modal, Row, Button, FormControl, Spinner } from 'react-bootstrap';
import { EmailRegex } from '../../../../../helpers/constants';
import PaymentReminderInvoiceTable from './PaymentReminderInvoiceTable';

const PaymentReminderModal = ({
  show,
  onHide,
  onSubmit,
  showProgress,
  loadingInvoices = false,
  invoices = [],
  orderTypeText,
  initialRecipientEmail = ''
}) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const showSelect = invoices.length > 1;
  const idsToSend = useMemo(() => {
    if (invoices.length === 1) return [String(invoices[0]._id)];
    return selectedIds;
  }, [invoices, selectedIds]);

  const isOpen = Boolean(show);

  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setError(false);
      setSelectedIds([]);
      return;
    }
    setEmail(initialRecipientEmail || '');
    setError(false);
    setSelectedIds([]);
  }, [isOpen, initialRecipientEmail]);

  useEffect(() => {
    if (invoices.length === 1) {
      setSelectedIds([String(invoices[0]._id)]);
      return;
    }
    const validIds = new Set(invoices.map(row => String(row._id)));
    setSelectedIds(prev => prev.filter(id => validIds.has(id)));
  }, [invoices]);

  const validateEmail = () => {
    if (!EmailRegex.test(email)) {
      setError(true);
      return false;
    }
    setError(false);
    return true;
  };

  const handleSend = async () => {
    if (!validateEmail() || !idsToSend.length) return;
    const failedIds = await onSubmit(email, idsToSend);
    if (Array.isArray(failedIds) && failedIds.length > 0) {
      setSelectedIds(failedIds.map(String));
    }
  };

  const busy = Boolean(showProgress);

  return (
    <Modal show={!!show} onHide={onHide} centered backdrop="static" size="lg">
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Payment Due</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted midFont">
          {showSelect
            ? `Select past-due unpaid invoices for this ${orderTypeText} order. A separate payment reminder email is sent for each selected invoice.`
            : `Send a payment reminder for the past-due unpaid invoice on this ${orderTypeText} order.`}
        </p>

        <h6 style={{ fontSize: 14 }}>Recipient email</h6>
        <FormControl
          type="email"
          className={`${error ? 'input-invalid' : ''} mb-3`}
          placeholder="email..."
          size="sm"
          value={email}
          disabled={busy}
          onChange={e => {
            setError(false);
            setEmail(e.target.value);
          }}
        />
        {error && <p className="xsmallFont text-rush mb-3 mt-n2">Enter a valid email address!</p>}

        <PaymentReminderInvoiceTable
          invoices={invoices}
          loading={loadingInvoices}
          selectedIds={selectedIds}
          showSelect={showSelect}
          disabled={busy}
          onToggleSelect={id =>
            setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
          }
          onToggleSelectAll={checked =>
            setSelectedIds(checked ? invoices.map(row => String(row._id)) : [])
          }
        />
      </Modal.Body>
      <Modal.Footer>
        <Row className="w-100">
          <Col className="d-flex justify-content-end">
            <Button disabled={busy} variant="secondary" onClick={onHide} className="mr-2">
              Go Back
            </Button>
            <Button
              disabled={busy || loadingInvoices || idsToSend.length === 0}
              variant="danger"
              onClick={handleSend}
              className="d-inline-flex align-items-center justify-content-center"
            >
              {busy && <Spinner size="sm" animation="border" className="mr-1" />}
              Send Reminder {idsToSend.length > 1 ? `(${idsToSend.length})` : ''}
            </Button>
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  );
};

export default PaymentReminderModal;
